/**
 * 서버 점검/연결 끊김 전역 오버레이.
 * 백엔드 /api/health 가 연속 실패할 때 이 컴포넌트가 마운트되어
 * 전체 UI 를 덮고 모든 상호작용 차단 + 자동 재시도 안내.
 *
 * MaintenanceGate 와 톤 일관 — "operations status page" 스타일.
 * 차이점: 이건 "예상치 못한 연결 실패" 케이스라 ETA 미정, 강조색은 red.
 */
import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FiRefreshCw, FiCheck, FiMail, FiExternalLink } from 'react-icons/fi'

interface Props {
  isDown: boolean
  lastOkAt: number | null
}

function formatKstTime(ts: number, locale: string): string {
  try {
    return new Date(ts).toLocaleTimeString(locale === 'en' ? 'en-US' : 'ko-KR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    })
  } catch {
    return '-'
  }
}

function elapsedSec(from: number, to: number): string {
  const s = Math.max(0, Math.floor((to - from) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

export default function ServerMaintenanceOverlay({ isDown, lastOkAt }: Props) {
  const { t, i18n } = useTranslation()
  const [retrying, setRetrying] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const [nextCheckIn, setNextCheckIn] = useState(15)

  // 실시간 갱신
  useEffect(() => {
    if (!isDown) return
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [isDown])

  // 15초 카운트다운
  useEffect(() => {
    if (!isDown) return
    const tick = setInterval(() => {
      setNextCheckIn((n) => {
        if (n <= 1) {
          // useBackendHealth 가 자체 폴링하므로 여기선 카운터만 리셋
          return 15
        }
        return n - 1
      })
    }, 1000)
    return () => clearInterval(tick)
  }, [isDown])

  const handleRetry = () => {
    setRetrying(true)
    setTimeout(() => window.location.reload(), 400)
  }

  const nowDate = new Date(now)
  const fmtDate = (d: Date) => d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  })

  return (
    <AnimatePresence>
      {isDown && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] overflow-y-auto bg-zinc-950"
          role="alertdialog"
          aria-live="assertive"
          aria-label={t('serverMaintenance.ariaLabel')}
        >
          {/* 배경 사진 */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src="/assets/hero/landing-hero-ambient.jpg"
              alt=""
              className="w-full h-full object-cover opacity-[0.10]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/85 to-zinc-950" />
          </div>

          <div className="relative min-h-screen flex flex-col">
            {/* 상단: 로고 + 시각 */}
            <header className="border-b border-white/[0.06]">
              <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: '"Tinos", "Times New Roman", serif' }}>
                  Heinrich
                </span>
                <span className="text-xs font-mono text-zinc-500 tabular-nums">
                  {fmtDate(nowDate)} · {formatKstTime(now, i18n.language)} KST
                </span>
              </div>
            </header>

            {/* 메인 */}
            <main className="flex-1 flex items-center justify-center px-6 py-12">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-2xl"
              >
                {/* 상태 라벨 */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-red-400">
                    {t('serverMaintenance.statusLabel', { defaultValue: 'Service Unavailable · Backend Unreachable' })}
                  </span>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4 leading-[1.15]">
                  {t('serverMaintenance.title', { defaultValue: '서버 연결이 일시적으로 끊겼습니다' })}
                </h1>
                <p
                  className="text-base text-zinc-300 leading-[1.75] mb-8 max-w-xl"
                  dangerouslySetInnerHTML={{ __html: t('serverMaintenance.description', {
                    defaultValue: '서비스가 일시적으로 응답하지 않습니다. 자동으로 재연결을 시도하고 있으며, 연결이 복구되는 대로 페이지가 다시 로드됩니다.',
                  })}}
                />

                {/* 상태 카드 */}
                <div className="grid grid-cols-2 gap-px bg-white/[0.08] rounded-md overflow-hidden border border-white/[0.08] mb-6">
                  <InfoBlock
                    label={t('serverMaintenance.lastOk', { defaultValue: '마지막 정상 응답' })}
                    value={lastOkAt
                      ? `${formatKstTime(lastOkAt, i18n.language)} (${elapsedSec(lastOkAt, now)} 전)`
                      : t('serverMaintenance.checking', { defaultValue: '확인 중...' })}
                    mono
                  />
                  <InfoBlock
                    label={t('serverMaintenance.autoRetry', { defaultValue: '자동 재확인까지' })}
                    value={`${nextCheckIn}s`}
                    mono
                    accent
                  />
                  <InfoBlock
                    label={t('serverMaintenance.statusCellLabel', { defaultValue: '연결 상태' })}
                    value={t('serverMaintenance.statusValueDown', { defaultValue: '응답 없음' })}
                    danger
                  />
                  <InfoBlock
                    label={t('serverMaintenance.dataLabel', { defaultValue: '데이터 안전성' })}
                    value={t('serverMaintenance.dataSafe', { defaultValue: '서버 보존' })}
                    ok
                  />
                </div>

                {/* 가능한 원인 */}
                <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-5 mb-8">
                  <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-500 mb-3">
                    {t('serverMaintenance.causesLabel', { defaultValue: '가능한 원인' })}
                  </div>
                  <ul className="space-y-2">
                    <CauseRow label={t('serverMaintenance.cause1', { defaultValue: '서버 재시작·배포 중 (보통 30초~2분 내 복귀)' })} />
                    <CauseRow label={t('serverMaintenance.cause2', { defaultValue: '예정되지 않은 일시적 장애' })} />
                    <CauseRow label={t('serverMaintenance.cause3', { defaultValue: '네트워크 연결 일시 끊김 (Wi-Fi/VPN 확인)' })} />
                  </ul>
                </div>

                {/* 액션 */}
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors disabled:opacity-60"
                  >
                    <FiRefreshCw size={14} className={retrying ? 'animate-spin' : ''} />
                    {retrying
                      ? t('serverMaintenance.retrying', { defaultValue: '재시도 중...' })
                      : t('serverMaintenance.retryNow', { defaultValue: '지금 새로고침' })}
                  </button>
                  <a
                    href="https://blog.naver.com/heinrich_dasan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-white/[0.04] border border-white/[0.12] text-zinc-200 hover:bg-white/[0.08] hover:border-white/[0.20] transition-colors"
                  >
                    <FiExternalLink size={13} />
                    {t('serverMaintenance.visitBlog', { defaultValue: '하인리히 블로그 보러가기' })}
                  </a>
                </div>
              </motion.div>
            </main>

            {/* 하단 푸터 */}
            <footer className="border-t border-white/[0.06] py-5">
              <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-zinc-500">
                <div className="flex items-center gap-2">
                  <FiMail size={11} />
                  <span>{t('serverMaintenance.contactLabel', { defaultValue: '문의' })}</span>
                  <a href="mailto:heinrich_dasan@naver.com" className="font-mono text-zinc-400 hover:text-amber-400 transition-colors">
                    heinrich_dasan@naver.com
                  </a>
                </div>
                <span className="font-mono">Heinrich · heinrich.dasancms.co.kr</span>
              </div>
            </footer>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function InfoBlock({ label, value, accent, ok, danger, mono }: { label: string; value: string; accent?: boolean; ok?: boolean; danger?: boolean; mono?: boolean }) {
  return (
    <div className="bg-zinc-950 px-5 py-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-500 mb-1.5">{label}</div>
      <div className={`text-sm font-semibold ${mono ? 'font-mono tabular-nums' : ''} ${
        accent ? 'text-amber-400' : ok ? 'text-emerald-400' : danger ? 'text-red-400' : 'text-white'
      }`}>
        {value}
      </div>
    </div>
  )
}

function CauseRow({ label }: { label: string }) {
  return (
    <li className="flex items-start gap-3 text-sm text-zinc-400 leading-snug">
      <FiCheck size={11} className="mt-1.5 shrink-0 text-zinc-600" />
      <span>{label}</span>
    </li>
  )
}
